<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import FormSection from "@/Components/FormSection.vue";
import TextInput from "@/Components/TextInput.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import DangerButton from "@/Components/DangerButton.vue";
import SearchSelect from "@/Components/SearchSelect.vue";
import SelectInput from "@/Components/SelectInput.vue";

import { useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { ref, computed, watch, nextTick } from "vue";

const props = defineProps({
  element: Object,
  clients: Array,
  garments: Array,
  departments: Array,
  fabrics: Array,
  colors: Array,
  sampleTypes: Array,
  readOnly: { type: Boolean, default: false },
});

const saving = ref(false);
const processingImage = ref(false);
const loadingCurrentImage = ref(false);

// ✅ ref para poder limpiar el input file (no se limpia solo)
const imageInputRef = ref(null);
const editorCanvasRef = ref(null);

const form = useForm({
  // ✅ dejamos POST y usamos method spoofing solo en SAVE normal (ver saveForm)
  _method: "POST",

  client: props.element?.client != null && props.element?.client !== ''
    ? Number(props.element.client)
    : null,
  department: props.element?.department ?? "",
  type_of_sample: props.element?.type_of_sample ?? null,
  garment: props.element?.garment ?? null,
  fabric: props.element?.fabric ?? "",
  color_id: props.element?.color_id ?? null,

  project_name: props.element?.project_name || "",
  // ✅ normaliza null/"null"
  project_description:
    props.element?.project_description == null || props.element?.project_description === "null"
      ? ""
      : props.element.project_description,

  // ✅ normaliza null/"null"
  notes: props.element?.notes == null || props.element?.notes === "null" ? "" : props.element.notes,

  status: props.element?.status ?? true,

  image: null,
  remove_image: false,
});

const clientOptions = computed(() =>
  (props.clients || [])
    .filter((client) => client?.client && (client.ID != null || client.id != null))
    .map((client) => ({
      label: client.client,
      value: Number(client.ID ?? client.id),
    }))
    .filter((option) => option.label && !Number.isNaN(option.value))
);

const clientModel = computed({
  get: () => (form.client != null && form.client !== "" ? Number(form.client) : null),
  set: (v) => {
    form.client = v == null ? "" : Number(v);
  },
});

const isInListById = (list, idKey, val) =>
  Array.isArray(list) && list.some((i) => String(i[idKey]) === String(val));

const tempImage = ref(null);
const sourceImage = ref(null);
const sourceImageObjectUrl = ref(null);
const selectedFileType = ref("image/png");
const selectedFileName = ref("lookbook-image.png");
const rotationDeg = ref(0);
const imageBounds = ref(null);
const cropRect = ref(null);
const isDraggingCrop = ref(false);
const cropStartPoint = ref({ x: 0, y: 0 });

watch(
  () => tempImage.value?.url,
  (newUrl, oldUrl) => {
    if (oldUrl && oldUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(oldUrl);
      } catch (e) { }
    }
  }
);

watch([sourceImage, rotationDeg, cropRect], async () => {
  await nextTick();
  renderEditorCanvas();
});

watch(editorCanvasRef, async () => {
  await nextTick();
  renderEditorCanvas();
});

const hasImageSelected = computed(() => !!tempImage.value);
const hasPendingImageAdjustments = computed(() => rotationDeg.value !== 0 || !!cropRect.value);

const getCanvasPoint = (event) => {
  const canvas = editorCanvasRef.value;
  if (!canvas) return null;

  const rect = canvas.getBoundingClientRect();

  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.width,
    y: ((event.clientY - rect.top) / rect.height) * canvas.height,
  };
};

const clampPointToImageBounds = (point) => {
  const bounds = imageBounds.value;
  if (!bounds || !point) return null;

  return {
    x: Math.max(bounds.x, Math.min(bounds.x + bounds.w, point.x)),
    y: Math.max(bounds.y, Math.min(bounds.y + bounds.h, point.y)),
  };
};

const getCanvasFromImageAndRotation = (img, rotation) => {
  const rotatedCanvas = document.createElement("canvas");
  const rotatedContext = rotatedCanvas.getContext("2d");
  if (!rotatedContext) return null;

  const normalized = ((rotation % 360) + 360) % 360;
  const isVertical = normalized === 90 || normalized === 270;

  rotatedCanvas.width = isVertical ? img.naturalHeight : img.naturalWidth;
  rotatedCanvas.height = isVertical ? img.naturalWidth : img.naturalHeight;

  rotatedContext.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
  rotatedContext.rotate((normalized * Math.PI) / 180);
  rotatedContext.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  return rotatedCanvas;
};

const renderEditorCanvas = () => {
  const canvas = editorCanvasRef.value;
  const img = sourceImage.value;
  if (!canvas || !img) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const maxWidth = 700;
  const maxHeight = 440;

  const normalizedRotation = ((rotationDeg.value % 360) + 360) % 360;
  const isVertical = normalizedRotation === 90 || normalizedRotation === 270;

  const rotatedW = isVertical ? img.naturalHeight : img.naturalWidth;
  const rotatedH = isVertical ? img.naturalWidth : img.naturalHeight;
  const scale = Math.min(maxWidth / rotatedW, maxHeight / rotatedH, 1);

  canvas.width = Math.max(1, Math.round(maxWidth));
  canvas.height = Math.max(1, Math.round(maxHeight));

  const drawW = Math.round(rotatedW * scale);
  const drawH = Math.round(rotatedH * scale);
  const drawX = Math.round((canvas.width - drawW) / 2);
  const drawY = Math.round((canvas.height - drawH) / 2);

  imageBounds.value = { x: drawX, y: drawY, w: drawW, h: drawH };

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((normalizedRotation * Math.PI) / 180);
  ctx.scale(scale, scale);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  ctx.restore();

  ctx.strokeStyle = "#d1d5db";
  ctx.lineWidth = 1;
  ctx.strokeRect(drawX, drawY, drawW, drawH);

  if (cropRect.value) {
    const rect = cropRect.value;
    const x = Math.max(drawX, Math.min(rect.x, drawX + drawW));
    const y = Math.max(drawY, Math.min(rect.y, drawY + drawH));
    const w = Math.max(1, Math.min(rect.w, drawX + drawW - x));
    const h = Math.max(1, Math.min(rect.h, drawY + drawH - y));

    ctx.strokeStyle = "#f97316";
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);
  }
};

const resetImageEditor = () => {
  if (sourceImageObjectUrl.value?.startsWith("blob:")) {
    try {
      URL.revokeObjectURL(sourceImageObjectUrl.value);
    } catch (e) { }
  }

  sourceImage.value = null;
  sourceImageObjectUrl.value = null;
  selectedFileType.value = "image/png";
  selectedFileName.value = "lookbook-image.png";
  rotationDeg.value = 0;
  imageBounds.value = null;
  cropRect.value = null;
  isDraggingCrop.value = false;
  cropStartPoint.value = { x: 0, y: 0 };
};

const loadSourceImage = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (sourceImageObjectUrl.value?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(sourceImageObjectUrl.value);
        } catch (e) { }
      }

      sourceImageObjectUrl.value = objectUrl;
      sourceImage.value = img;
      resolve();
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Invalid image file"));
    };
    img.src = objectUrl;
  });

const rotateLeft = () => {
  if (!sourceImage.value) return;
  rotationDeg.value = (rotationDeg.value + 270) % 360;
};

const rotateRight = () => {
  if (!sourceImage.value) return;
  rotationDeg.value = (rotationDeg.value + 90) % 360;
};

const resetAdjustments = () => {
  rotationDeg.value = 0;
  cropRect.value = null;
};

const onEditorPointerDown = (event) => {
  if (!sourceImage.value || !imageBounds.value || props.readOnly) return;

  const point = clampPointToImageBounds(getCanvasPoint(event));
  if (!point) return;

  isDraggingCrop.value = true;
  cropStartPoint.value = point;
  cropRect.value = { x: point.x, y: point.y, w: 1, h: 1 };
};

const onEditorPointerMove = (event) => {
  if (!isDraggingCrop.value || !imageBounds.value) return;

  const point = clampPointToImageBounds(getCanvasPoint(event));
  if (!point) return;

  const sx = cropStartPoint.value.x;
  const sy = cropStartPoint.value.y;

  const x = Math.min(sx, point.x);
  const y = Math.min(sy, point.y);
  const w = Math.abs(point.x - sx);
  const h = Math.abs(point.y - sy);

  cropRect.value = { x, y, w, h };
};

const onEditorPointerUp = () => {
  if (!isDraggingCrop.value) return;
  isDraggingCrop.value = false;

  if (!cropRect.value) return;
  if (cropRect.value.w < 8 || cropRect.value.h < 8) {
    cropRect.value = null;
  }
};

const buildAdjustedFileName = (baseName, mimeType) => {
  const sanitized = (baseName || "lookbook-image")
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-z0-9\-_]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  const extensionMap = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
  };

  const ext = extensionMap[mimeType] || "png";
  return `${sanitized || "lookbook-image"}-edited.${ext}`;
};

const applyImageAdjustments = async () => {
  if (!sourceImage.value || !tempImage.value) return;

  if (!hasPendingImageAdjustments.value) return;

  processingImage.value = true;

  try {
    const rotatedCanvas = getCanvasFromImageAndRotation(sourceImage.value, rotationDeg.value);
    if (!rotatedCanvas) return;

    const rect = cropRect.value;
    const bounds = imageBounds.value;

    let sourceX = 0;
    let sourceY = 0;
    let sourceW = rotatedCanvas.width;
    let sourceH = rotatedCanvas.height;

    if (rect && bounds && rect.w > 0 && rect.h > 0) {
      const scaleX = rotatedCanvas.width / bounds.w;
      const scaleY = rotatedCanvas.height / bounds.h;

      sourceX = Math.max(0, Math.round((rect.x - bounds.x) * scaleX));
      sourceY = Math.max(0, Math.round((rect.y - bounds.y) * scaleY));
      sourceW = Math.min(rotatedCanvas.width - sourceX, Math.max(1, Math.round(rect.w * scaleX)));
      sourceH = Math.min(rotatedCanvas.height - sourceY, Math.max(1, Math.round(rect.h * scaleY)));
    }

    const outputCanvas = document.createElement("canvas");
    outputCanvas.width = sourceW;
    outputCanvas.height = sourceH;

    const outputContext = outputCanvas.getContext("2d");
    if (!outputContext) return;

    outputContext.drawImage(
      rotatedCanvas,
      sourceX,
      sourceY,
      sourceW,
      sourceH,
      0,
      0,
      sourceW,
      sourceH
    );

    const outputType =
      selectedFileType.value === "image/jpeg" ||
        selectedFileType.value === "image/png" ||
        selectedFileType.value === "image/webp"
        ? selectedFileType.value
        : "image/png";

    const blob = await new Promise((resolve, reject) => {
      outputCanvas.toBlob((createdBlob) => {
        if (!createdBlob) {
          reject(new Error("Could not process image"));
          return;
        }
        resolve(createdBlob);
      }, outputType);
    });

    const editedFile = new File([blob], buildAdjustedFileName(selectedFileName.value, outputType), {
      type: outputType,
      lastModified: Date.now(),
    });

    form.image = editedFile;
    form.remove_image = false;

    tempImage.value = {
      name: editedFile.name,
      url: URL.createObjectURL(editedFile),
    };

    await loadSourceImage(editedFile);
    resetAdjustments();
    form.clearErrors("image");
  } finally {
    processingImage.value = false;
  }
};

// ✅ helper único para limpiar todo lo relacionado a imagen (form + preview + input)
function resetLocalImageState() {
  form.image = null;
  form.remove_image = false;
  tempImage.value = null;
  resetImageEditor();

  // limpiar el input de archivo real
  if (imageInputRef.value) {
    imageInputRef.value.value = "";
  }
}

async function onSingleImageSelected(e) {
  const file = (e.target.files || [])[0] || null;

  form.image = file;

  if (file) form.remove_image = false;

  tempImage.value = file ? { name: file.name, url: URL.createObjectURL(file) } : null;
  if (file) {
    selectedFileType.value = file.type || "image/png";
    selectedFileName.value = file.name || "lookbook-image.png";
    resetAdjustments();
    try {
      await loadSourceImage(file);
    } catch (error) {
      resetLocalImageState();
      form.setError("image", "The selected file is not a valid image.");
      return;
    }
  } else {
    resetImageEditor();
  }

  if (file) form.clearErrors("image");
}

const loadCurrentImageForEditing = async () => {
  if (!props.element?.url || loadingCurrentImage.value) return;

  loadingCurrentImage.value = true;

  try {
    const response = await fetch(props.element.url, { credentials: "include" });
    if (!response.ok) {
      throw new Error("Could not fetch current image");
    }

    const blob = await response.blob();
    const mimeType = blob.type || "image/jpeg";
    const fileName = props.element.file_name || `lookbook-element-${props.element?.id || "image"}.jpg`;
    const file = new File([blob], fileName, {
      type: mimeType,
      lastModified: Date.now(),
    });

    selectedFileType.value = file.type || "image/png";
    selectedFileName.value = file.name || "lookbook-image.png";
    form.remove_image = false;

    tempImage.value = { name: file.name, url: URL.createObjectURL(file) };
    resetAdjustments();
    await loadSourceImage(file);

    // Solo enviamos una imagen nueva al backend cuando se apliquen ajustes.
    form.image = null;
    form.clearErrors("image");
  } catch (error) {
    form.setError("image", "Could not load the current image for editing.");
  } finally {
    loadingCurrentImage.value = false;
  }
};

function clearSelectedTempImage() {
  resetLocalImageState();
}

function markRemoveCurrentImage() {
  form.remove_image = true;
  form.image = null;
  tempImage.value = null;
  resetImageEditor();

  // limpiar input también, para evitar que quede un file seleccionado
  if (imageInputRef.value) imageInputRef.value.value = "";

  form.clearErrors("image");
}

function undoRemoveCurrentImage() {
  form.remove_image = false;
}

const goBack = () => {
  if (typeof history !== "undefined" && history.length > 1) history.back();
  else Inertia.get(route("sales.lookbook.index"), {}, { preserveState: true, preserveScroll: true });
};

// ✅ cuando cambia el elemento (ej: edit -> create por Save & Copy),
// Inertia puede reciclar el componente y mantener estado local.
// Acá forzamos reset de notes + imagen.
watch(
  () => props.element?.id,
  () => {
    // limpiar imagen local siempre al cambiar de "element"
    resetLocalImageState();

    // si el server manda notes null/"null", aseguramos vacío
    if (props.element?.notes == null || props.element?.notes === "null") form.notes = "";

    // si el server manda project_description null/"null"
    if (props.element?.project_description == null || props.element?.project_description === "null")
      form.project_description = "";
  }
);

const validateAndScroll = () => {
  const required = [
    { key: "client", label: "Client", el: "client" },
    { key: "department", label: "Department", el: "department" },
    { key: "type_of_sample", label: "Type of samples", el: "type_of_sample" },
    { key: "project_name", label: "Project name", el: "project_name" },
    { key: "garment", label: "Garment", el: "garment" },
    { key: "fabric", label: "Fabric", el: "fabric" },
  ];

  required.forEach((f) => form.clearErrors(f.key));
  form.clearErrors("image");

  let firstInvalid = null;

  required.forEach(({ key, label, el }) => {
    const v = form[key];
    const missing = v === null || v === undefined || v === "";
    if (missing) {
      form.setError(key, `${label} is required.`);
      if (!firstInvalid) firstInvalid = el;
    }
  });

  const isEdit = !!props.element?.id;

  // Si estoy creando, tengo que tener imagen sí o sí.
  if (!isEdit && !form.image) {
    form.setError("image", "You must upload an image.");
    if (!firstInvalid) firstInvalid = "image-input";
  }

  if (firstInvalid) {
    document.getElementById(firstInvalid)?.scrollIntoView({ behavior: "smooth", block: "center" });
    return false;
  }

  return true;
};

const saveForm = () => {
  if (!validateAndScroll()) return;

  submitForm(false);
};

const submitForm = async (asCopy = false) => {
  if (processingImage.value || saving.value) return;

  if (hasPendingImageAdjustments.value) {
    await applyImageAdjustments();
  }

  saving.value = true;

  const isEdit = !!props.element?.id;

  const endpoint = asCopy
    ? isEdit
      ? route("sales.lookbook.saveAndCopy.update", { lookbookElement: props.element.id })
      : route("sales.lookbook.saveAndCopy.create")
    : isEdit
      ? route("sales.lookbook.update", { lookbookElement: props.element.id })
      : route("sales.lookbook.store");

  const prevMethod = form._method;
  form._method = asCopy ? "POST" : isEdit ? "PUT" : "POST";

  form.post(endpoint, {
    preserveScroll: true,
    forceFormData: true,
    onFinish() {
      form._method = prevMethod;
      saving.value = false;

      if (asCopy) {
        form.notes = "";
        resetLocalImageState();
      }
    },
  });
};

const saveAndCopy = () => {
  if (!validateAndScroll()) return;
  submitForm(true);
};

// (Estas funciones quedan por si tu layout las usa o las vas a reutilizar;
// no afectan el Save & Copy.)
const showPreviewModal = ref(false);
const elementToPreview = ref(null);

const openPreview = (el) => {
  elementToPreview.value = el;
  showPreviewModal.value = true;
};

const closePreview = () => {
  showPreviewModal.value = false;
  elementToPreview.value = null;
};

const goEdit = () => {
  if (!elementToPreview.value) return;
  window.location.href = route("sales.lookbook.edit", elementToPreview.value.id);
};

const downloadPdf = () => {
  if (!elementToPreview.value) return;
  window.location.href = route("sales.lookbook.pdf", elementToPreview.value.id);
};

const downloadImage = async () => {
  const el = elementToPreview.value;
  if (!el?.url) return;

  try {
    const res = await fetch(el.url, { credentials: "include" });
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = objectUrl;

    const ext = (el.file_name || "image").split(".").pop();
    const safeName = (el.project_name || "lookbook").toString().replace(/[^a-z0-9\-_]+/gi, "-");

    a.download = `${safeName}-${el.id}.${ext || "png"}`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(objectUrl);
  } catch (e) {
    window.open(el.url, "_blank");
  }
};
</script>

<template>
  <AppLayout title="Lookbook Element">
    <template #title>
      <a href="#" @click.prevent="goBack">
        <SecondaryButton>Back</SecondaryButton>
      </a>
    </template>

    <div class="max-w-7xl mx-auto py-5 sm:px-6 lg:px-8">
      <FormSection>
        <template #title>
          <a href="#" @click.prevent="goBack">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <!-- Client -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Client" id="client" :required="true" />
            <SearchSelect v-model="clientModel" :options="clientOptions" placeholder="Select a client" />
            <InputError :message="form.errors.client" class="mt-2" />
          </div>

          <!-- Department -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Department" :required="true" />
            <SelectInput
              class="mt-1"
              v-model="form.department" id="department" :disabled="readOnly">
              <option v-if="!form.department" disabled value="">Select a department</option>

              <option v-if="form.department && !isInListById(departments, 'id', form.department)"
                :value="form.department">
                {{ form.department }}
              </option>

              <option v-for="dep in departments" :key="dep.id" :value="String(dep.id)">
                {{ dep.department }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.department" class="mt-2" />
          </div>

          <!-- Type of samples -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Type of samples" :required="true" />
            <SelectInput
              class="mt-1"
              v-model="form.type_of_sample" id="type_of_sample" :disabled="readOnly">
              <option v-if="!form.type_of_sample" disabled value="">Select type of sample</option>
              <option v-for="t in sampleTypes" :key="t.type_id" :value="t.type_id">
                {{ t.sample_type }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.type_of_sample" class="mt-2" />
          </div>

          <!-- Project Name -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Project Name" :required="true" />
            <TextInput id="project_name" v-model="form.project_name" type="text" class="mt-1 block w-full"
              :disabled="readOnly" />
            <InputError :message="form.errors.project_name" class="mt-2" />
          </div>

          <!-- Garment -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Garment" :required="true" />
            <SelectInput
              class="mt-1"
              v-model="form.garment" id="garment" :disabled="readOnly">
              <option v-if="!form.garment" disabled value="">Select a garment</option>
              <option v-for="g in garments" :key="g.garmentID" :value="g.garmentID">
                {{ g.garmentName }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.garment" class="mt-2" />
          </div>

          <!-- Fabric -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Fabric" :required="true" />
            <SelectInput
              class="mt-1"
              v-model="form.fabric" id="fabric" :disabled="readOnly">
              <option v-if="!form.fabric" disabled value="">Select a fabric</option>

              <option v-if="form.fabric && !isInListById(fabrics, 'fabricID', form.fabric)" :value="form.fabric">
                {{ form.fabric }}
              </option>

              <option v-for="fab in fabrics" :key="fab.fabricID" :value="String(fab.fabricID)">
                {{ fab.fabName }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.fabric" class="mt-2" />
          </div>

          <!-- Color -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Color" />
            <SelectInput
              class="mt-1"
              v-model="form.color_id" id="color_id" :disabled="readOnly">
              <option :value="null">Select a color</option>

              <option v-if="form.color_id && !isInListById(colors, 'id', form.color_id)" :value="form.color_id">
                {{ form.color_id }}
              </option>

              <option v-for="color in colors" :key="color.id" :value="String(color.id)">
                {{ color.name }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.color_id" class="mt-2" />
          </div>

          <!-- Project Description -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Project Description" />
            <textarea rows="8" id="project_description" v-model="form.project_description"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
              :disabled="readOnly" />
            <InputError :message="form.errors.project_description" class="mt-2" />
          </div>

          <!-- Notes -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Notes" />
            <textarea rows="8" id="notes" v-model="form.notes"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
              :disabled="readOnly" />
            <InputError :message="form.errors.notes" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <br />

      <FormSection :noGrid="true">
        <template #title>Image</template>

        <template #form>
          <div v-if="element?.id" class="space-y-3">
            <div class="flex items-start gap-4">
              <div class="w-36">
                <img v-if="element.url && !form.remove_image" :src="element.url"
                  class="h-28 w-28 object-cover rounded border" :alt="element.file_name" />
                <div v-else class="h-28 w-28 rounded border flex items-center justify-center text-gray-400">—</div>
              </div>

              <div class="flex-1 space-y-2">
                <div class="text-sm text-gray-700">
                  <span class="font-semibold">Current:</span>
                  <span v-if="element.file_name && !form.remove_image">{{ element.file_name }}</span>
                  <span v-else class="text-gray-500">No image</span>
                </div>

                <div class="flex gap-2">
                  <DangerButton type="button" @click="markRemoveCurrentImage"
                    :disabled="readOnly || !element.file_name || form.remove_image" title="Remove current image">
                    Remove image
                  </DangerButton>

                  <SecondaryButton v-if="form.remove_image" type="button" @click="undoRemoveCurrentImage"
                    :disabled="readOnly" title="Undo remove">
                    Undo
                  </SecondaryButton>

                  <SecondaryButton v-if="!form.remove_image && element.url" type="button"
                    @click="loadCurrentImageForEditing"
                    :disabled="readOnly || saving || processingImage || loadingCurrentImage"
                    title="Load current image into the editor">
                    {{ loadingCurrentImage ? "Loading..." : "Adjust Current Image" }}
                  </SecondaryButton>
                </div>

                <p v-if="form.remove_image" class="text-sm text-amber-700">
                  Image will be removed when you click <b>Save</b>.
                </p>
              </div>
            </div>
          </div>

          <div class="space-y-3" :class="element?.id ? 'mt-6' : ''">
            <InputLabel :value="element?.id ? 'Replace image (only 1)' : 'Upload image (only 1)'"
              :required="!element?.id" />

            <div class="flex justify-center items-center w-full">
              <label for="image-input"
                class="flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-gray-100 border-gray-300">
                <div class="flex flex-col justify-center items-center pt-5 pb-6">
                  <p class="mb-2 text-sm text-gray-500">
                    <span class="font-semibold">Click to upload</span> (1 image)
                  </p>
                  <p class="text-xs text-gray-500">Max. File Size: 10MB</p>
                </div>

                <input ref="imageInputRef" id="image-input" type="file" accept="image/*" @change="onSingleImageSelected"
                  class="hidden" :disabled="readOnly" />
              </label>
            </div>

            <div class="mt-4 space-y-3" v-if="tempImage">
              <div class="flex items-center justify-between gap-4">
                <div class="text-sm text-gray-700 truncate">
                  <span class="font-semibold">Selected:</span> {{ tempImage.name }}
                </div>

                <DangerButton type="button" @click="clearSelectedTempImage" title="Remove selected image"
                  :disabled="readOnly || saving || processingImage || loadingCurrentImage">
                  Remove
                </DangerButton>
              </div>

              <div class="rounded-lg border bg-white p-3">
                <canvas ref="editorCanvasRef"
                  class="w-full max-h-[440px] rounded border border-gray-200 cursor-crosshair select-none"
                  @mousedown="onEditorPointerDown" @mousemove="onEditorPointerMove" @mouseup="onEditorPointerUp"
                  @mouseleave="onEditorPointerUp" />
              </div>

              <div class="flex flex-wrap gap-2">
                <SecondaryButton type="button" @click="rotateLeft"
                  :disabled="readOnly || saving || processingImage || loadingCurrentImage || !hasImageSelected">
                  Rotate Left
                </SecondaryButton>
                <SecondaryButton type="button" @click="rotateRight"
                  :disabled="readOnly || saving || processingImage || loadingCurrentImage || !hasImageSelected">
                  Rotate Right
                </SecondaryButton>
                <PrimaryButton type="button" @click="applyImageAdjustments" :disabled="readOnly || saving || processingImage || loadingCurrentImage || !hasPendingImageAdjustments
                  ">
                  {{ processingImage ? "Processing..." : "Apply Crop/Rotation" }}
                </PrimaryButton>
              </div>

              <p class="text-xs text-gray-600">
                Drag on the image to select crop area. If you save without applying, adjustments are applied
                automatically.
              </p>
            </div>

            <InputError :message="form.errors.image" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <br />

      <FormSection :noGrid="true">
        <template #actions>
          <a href="#" class="mr-5" v-if="!saving" @click.prevent="goBack">
            <SecondaryButton type="button">Back</SecondaryButton>
          </a>

          <SecondaryButton type="button" class="mr-3" @click.prevent="saveAndCopy"
            :disabled="saving || processingImage || loadingCurrentImage || readOnly"
            title="Save this element and open a new one with the same data (without notes or image)">
            Save & Copy
          </SecondaryButton>

          <PrimaryButton type="button" @click.prevent="saveForm"
            :disabled="saving || processingImage || loadingCurrentImage || readOnly">
            Save
          </PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
